import { HomeReview } from '../types';
import { OfflineReview } from './offlineDatabase';

export interface OfflineReviewMutationDatabase {
  updateReview(reviewId: number, updates: Partial<OfflineReview>): Promise<void>;
  getReview?(reviewId: number): Promise<OfflineReview | null>;
  recordOfflineMutation?(entry: {
    entityType: 'review';
    entityId?: number | null;
    reviewId?: number | null;
    mutationType: string;
    payload?: unknown;
    previousSnapshot?: unknown;
    status?: 'pending' | 'synced' | 'failed' | 'conflict';
  }): Promise<number | undefined>;
  createRecoverySnapshot?(reviewId: number, reason: string): Promise<void>;
}

export interface OfflineReviewMutationCallbacks {
  enqueueReviewUpdate(reviewId: number): void;
  getOfflineReview(reviewId: number): Promise<HomeReview>;
}

type OfflineReviewMutationLogger = Pick<Console, 'warn'>;

export class OfflineReviewMutationService {
  constructor(
    private readonly database: OfflineReviewMutationDatabase,
    private readonly callbacks: OfflineReviewMutationCallbacks,
    private readonly logger: OfflineReviewMutationLogger = console
  ) {}

  async updateOfflineReview(
    reviewId: number,
    updates: Partial<HomeReview>
  ): Promise<HomeReview> {
    try {
      const previousSnapshot = await this.database.getReview?.(reviewId).catch(() => null) || null;
      const offlineUpdates = this.mapHomeReviewUpdates(updates, previousSnapshot);

      await this.database.updateReview(reviewId, offlineUpdates);
      await this.database.recordOfflineMutation?.({
        entityType: 'review',
        entityId: reviewId,
        reviewId,
        mutationType: 'review_update',
        payload: updates,
        previousSnapshot,
        status: 'pending',
      });
      await this.database.createRecoverySnapshot?.(reviewId, 'review_update');
      this.callbacks.enqueueReviewUpdate(reviewId);

      return await this.callbacks.getOfflineReview(reviewId);
    } catch (error) {
      this.logger.warn('[OfflineReviewService] Error updating offline review:', error);
      throw error;
    }
  }

  private mapHomeReviewUpdates(
    updates: Partial<HomeReview>,
    previousSnapshot: OfflineReview | null
  ): Partial<OfflineReview> {
    const offlineUpdates: Partial<OfflineReview> = {};

    if (updates.status !== undefined) offlineUpdates.status = updates.status;
    if (updates.completedAt !== undefined) offlineUpdates.completedAt = updates.completedAt;
    if (updates.submittedAt !== undefined) offlineUpdates.submittedAt = updates.submittedAt;
    if (updates.notes !== undefined) offlineUpdates.notes = updates.notes;
    if (updates.onDemandProjects !== undefined) offlineUpdates.onDemandProjects = updates.onDemandProjects;
    if (updates.vendorNotes !== undefined) offlineUpdates.vendorNotes = updates.vendorNotes;
    if (updates.memberName !== undefined) offlineUpdates.memberName = updates.memberName;
    if (updates.memberEmail !== undefined) offlineUpdates.memberEmail = updates.memberEmail;
    if (updates.homeManagerName !== undefined) offlineUpdates.homeManagerName = updates.homeManagerName;
    if (updates.homeManagerEmail !== undefined) offlineUpdates.homeManagerEmail = updates.homeManagerEmail;
    if (updates.metadata !== undefined) offlineUpdates.metadata = updates.metadata;

    if (
      updates.status !== undefined ||
      updates.completedAt !== undefined ||
      updates.submittedAt !== undefined ||
      updates.notes !== undefined ||
      updates.onDemandProjects !== undefined ||
      updates.vendorNotes !== undefined ||
      updates.memberName !== undefined ||
      updates.memberEmail !== undefined ||
      updates.homeManagerName !== undefined ||
      updates.homeManagerEmail !== undefined ||
      updates.metadata !== undefined
    ) {
      offlineUpdates.syncStatus = 'pending';
      offlineUpdates.lastLocalMutationAt = new Date();
      offlineUpdates.localRevision = (previousSnapshot?.localRevision ?? 0) + 1;
      offlineUpdates.conflictStatus = 'none';
    }

    return offlineUpdates;
  }
}
